import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/** Longest wait for the next hero image before swapping the gallery anyway. */
const LEAD_IMAGE_TIMEOUT_MS = 2500;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  /** Increments per product selection so a slower, older response cannot replace a newer one. */
  #selectionId = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const selectionId = ++this.#selectionId;

    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // Non-hero images are lazy, so the new colourway's hero is usually not cached yet.
        // Keep the current gallery visible until it decodes (or a timeout passes).
        await this.#preloadLeadImage(newMediaGallery);
        if (selectionId !== this.#selectionId || !this.isConnected) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Fetches and decodes the image the incoming gallery will show first, using its own
   * srcset and sizes so the inserted element reuses the same response.
   *
   * @param {Element} gallery - The incoming media gallery.
   * @returns {Promise<void>} Settles once decoded, on failure, or after the timeout.
   */
  #preloadLeadImage(gallery) {
    const showsGrid =
      gallery instanceof HTMLElement &&
      gallery.dataset.presentation === 'grid' &&
      window.matchMedia('(min-width: 750px)').matches;
    const selector = showsGrid ? '.media-gallery__grid .product-media__image' : 'slideshow-slide .product-media__image';
    const source = gallery.querySelector(selector);
    if (!(source instanceof HTMLImageElement)) return Promise.resolve();

    const image = new Image();
    image.sizes = source.sizes;
    image.srcset = source.srcset;
    image.src = source.src;

    return Promise.race([
      image.decode().catch(() => {}),
      new Promise((resolve) => setTimeout(resolve, LEAD_IMAGE_TIMEOUT_MS)),
    ]);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
